import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ArrangementNoticesDto, MarkNotificationsSeenDto, NotificationsDto } from '@pams/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { can } from '@/lib/accessRole';
import { useAuth } from '@/hooks/useAuth';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [C1g §1d] THE BELL'S DATA.
 *
 *  ⚠️ IT IS GATED ON THE SAME CAPABILITY THE ROUTE IS. `arrangement.read`, read through
 *  `can` rather than compared against a role string — §10's rule, and [P6d]'s receipt: a role
 *  comparison on the client beside a capability on the server is a control that appears for
 *  somebody every request behind it refuses. An officer without it never fires the query, so
 *  the bell costs them nothing at all.
 *
 *  ⚠️ `staleTime` IS DELIBERATELY GENEROUS, BECAUSE THIS RUNS FROM THE APP CHROME.
 *  `UtilityBar` is on EVERY dashboard page, so without it a nodal walking between screens
 *  would re-run a blocking `$group` over their whole scope on each one. Sixty seconds is the
 *  same figure `useOrgUnitCounts` settled on for the same reason.
 *
 *  ⚠️ AND `refetchOnWindowFocus` IS LEFT ALONE. The count is work-to-do rather than a live
 *  reading, and CLAUDE.md §8 bans `refetchIntervalInBackground` — a bell that polls is a bell
 *  that costs a query per minute per open tab for a number that changes when somebody acts.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export type UseNotifications = {
  data: NotificationsDto | undefined;
  /** [P7B §6b] The caller's own notices — "an order you were on was taken back to draft". */
  notices: ArrangementNoticesDto | undefined;
  isLoading: boolean;
  /** `false` for a role that cannot read arrangements — the bell is not rendered at all. */
  enabled: boolean;
  markSeen: () => void;
};

export const useNotifications = (): UseNotifications => {
  const { officer } = useAuth();
  const queryClient = useQueryClient();
  const enabled = officer !== undefined && officer !== null && can(officer.accessRole, 'arrangement.read');

  const query = useQuery({
    queryKey: queryKeys.notifications.list(),
    queryFn: ({ signal }) => api.get<NotificationsDto>('/notifications', { signal }),
    enabled,
    staleTime: 60_000,
  });

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [P7B §6b] A SECOND QUERY, AND IT STAYS SECOND — never folded into the bell's payload.
   *
   *  The bell answers "what work is LEFT" and is derived on every read; a notice records that
   *  something was TOLD, which a derived count cannot remember ([C3c.1 §6d]'s two mechanisms). So the
   *  notices are their own read, under their own key, on the SAME gate and the same sixty seconds —
   *  one extra request a minute per signed-in dashboard user, which the plan states (§1h).
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const notices = useQuery({
    queryKey: queryKeys.notifications.notices(),
    queryFn: ({ signal }) => api.get<ArrangementNoticesDto>('/notifications/notices', { signal }),
    enabled,
    staleTime: 60_000,
  });

  /**
   * ⚠️ THE ANSWER IS WRITTEN BACK WITH `setQueryData`, NEVER INVALIDATED.
   *
   * [P4]'s hot-path rule: an invalidation schedules a refetch, and the refetch would re-run
   * the aggregation purely to learn a timestamp the response already told us. It also cannot
   * change `total` — marking seen clears NOVELTY and never work (§1b).
   *
   * ⚠️ IT CLEARS THE SUMMARY AND **NOT** THE PER-ENTRY DOTS, AND THAT IS THE WHOLE POINT.
   *
   * The first version re-stamped `entries[].isNew` to `false` here, and the effect was that
   * the dots vanished in the same tick the panel appeared: the operator opened the bell to
   * find out what was new and was shown a list with nothing marked. `newCount` is what the
   * BELL advertises, so it clears — the bell has been read. The dots describe the list they
   * are IN, and they survive until the next fetch answers honestly.
   */
  const seen = useMutation({
    mutationFn: () => api.post<MarkNotificationsSeenDto>('/notifications/seen', {}),
    onSuccess: (result) => {
      queryClient.setQueryData<NotificationsDto>(queryKeys.notifications.list(), (previous) =>
        previous ? { ...previous, seenAt: result.seenAt, newCount: 0 } : previous,
      );
    },
  });

  /**
   * ⚠️ [P7B §6b] OPENING THE BELL MARKS THE CALLER'S NOTICES SEEN — only when some are unseen, and the
   * answer's COUNT is written back while the items on screen keep their dots: `seen`'s rule above, for
   * the same reason. The number in the accessible name clears; the list the operator opened to read
   * still says which rows were new until the next fetch.
   */
  const noticesSeen = useMutation({
    mutationFn: () => api.post<ArrangementNoticesDto>('/notifications/notices/seen', {}),
    onSuccess: (result) => {
      queryClient.setQueryData<ArrangementNoticesDto>(queryKeys.notifications.notices(), (previous) =>
        previous ? { ...previous, total: result.total, unseenCount: result.unseenCount } : result,
      );
    },
  });

  return {
    data: query.data,
    notices: notices.data,
    isLoading: query.isLoading,
    enabled,
    markSeen: () => {
      /* ⚠️ Only when there is something to clear — an open with nothing new writes nothing. */
      if ((query.data?.newCount ?? 0) > 0) seen.mutate();
      if ((notices.data?.unseenCount ?? 0) > 0) noticesSeen.mutate();
    },
  };
};
